/** Pure client-side filtering helpers for the cheapest-itineraries table (CHO-611).
 *
 * This module intentionally imports no React/JSX so that server-side unit tests
 * can require it directly via relative path.
 */

import { hasStopover, tripShape } from '../../../../server/lib/flight-shape.ts';

export interface FlightFilterOptions {
  home_origin: string | null;
  outbound: { min: string; max: string } | null;
  inbound: { min: string; max: string } | null;
  stopover_nights: { min: number; max: number; values: number[] } | null;
  stopover_legs: string[];
  carriers: string[];
}

export type StopoverPresence = 'any' | 'direct' | 'stopover';

/** Fixed domain for the stopover-nights slider.
 *
 * Starting at 1 (not 0) keeps "no stopover" off this axis; absence of a stopover
 * is expressed exclusively through the stopover-presence toggle.
 */
export const NIGHTS_DOMAIN_MIN = 1;
export const NIGHTS_DOMAIN_MAX = 14;

export interface FlightFilterState {
  outbound: [string, string] | null;
  inbound: [string, string] | null;
  nights: [number, number] | null;
  leg: string | null;
  carriers: string[];
  stopover: StopoverPresence;
}

export type ItineraryGroupKey = 'ONEWAY:OUTBOUND' | 'ONEWAY:INBOUND' | 'ROUNDTRIP';

export interface FilterableRow {
  itinerary_shape: 'oneway' | 'return' | 'stopover';
  leg_direction: 'outbound' | 'inbound';
  inbound_date: string | null;
  effective_outbound_date: string | null;
  effective_inbound_date: string | null;
  stopover_point: string | null;
  stopover_leg: string | null;
  stopover_nights: number | null;
  carrier: string;
}

export interface ShapeGroup<T extends FilterableRow> {
  group_key: ItineraryGroupKey;
  itinerary_shape: T['itinerary_shape']; // representative raw shape of the group, kept for back-compat
  count: number;
  rows: T[];
}

/** Composite group key used for splitting cheapest-itinerary rows by travel direction.
 *
 * Stopover is an attribute, not a shape: a stopover row is classified by its
 * underlying one-way vs. round-trip shape. Rows with a return date are grouped
 * as ROUNDTRIP; one-way stopovers are split by leg_direction like any other
 * one-way row. The stopover filters (nights / leg) still apply independently.
 */
export function itineraryGroupKey(row: FilterableRow): ItineraryGroupKey {
  const shape = tripShape(row);
  if (shape === 'return') {
    return 'ROUNDTRIP';
  }
  return row.leg_direction === 'inbound' ? 'ONEWAY:INBOUND' : 'ONEWAY:OUTBOUND';
}

/** Build the default (full-range) filter state for the given options. */
export function defaultFilterState(options: FlightFilterOptions): FlightFilterState {
  return {
    outbound: options.outbound ? [options.outbound.min, options.outbound.max] : null,
    inbound: options.inbound ? [options.inbound.min, options.inbound.max] : null,
    nights: [NIGHTS_DOMAIN_MIN, NIGHTS_DOMAIN_MAX],
    leg: null,
    carriers: [],
    stopover: 'any',
  };
}

/** Clamp/adjust an existing filter state so it stays valid after a refetch. */
export function reconcileFilterState(prev: FlightFilterState, options: FlightFilterOptions): FlightFilterState {
  return {
    outbound: clampRange(prev.outbound, options.outbound),
    inbound: clampRange(prev.inbound, options.inbound),
    nights: clampNumericRange(prev.nights, { min: NIGHTS_DOMAIN_MIN, max: NIGHTS_DOMAIN_MAX }),
    leg: prev.leg,
    carriers: prev.carriers.filter((c) => options.carriers.includes(c)),
    stopover: prev.stopover ?? 'any',
  };
}

function clampRange(
  current: [string, string] | null,
  bounds: { min: string; max: string } | null,
): [string, string] | null {
  if (!bounds) return null;
  if (!current) return [bounds.min, bounds.max];
  const low = current[0] < bounds.min ? bounds.min : current[0] > bounds.max ? bounds.max : current[0];
  const high = current[1] > bounds.max ? bounds.max : current[1] < bounds.min ? bounds.min : current[1];
  return low <= high ? [low, high] : [bounds.min, bounds.max];
}

function clampNumericRange(
  current: [number, number] | null,
  bounds: { min: number; max: number } | null,
): [number, number] | null {
  if (!bounds) return null;
  if (!current) return [bounds.min, bounds.max];
  const low = current[0] < bounds.min ? bounds.min : current[0] > bounds.max ? bounds.max : current[0];
  const high = current[1] > bounds.max ? bounds.max : current[1] < bounds.min ? bounds.min : current[1];
  return low <= high ? [low, high] : [bounds.min, bounds.max];
}

/** Decide whether a single row passes the active filters. */
export function filterFlightRows<T extends FilterableRow>(rows: T[], state: FlightFilterState): T[] {
  return rows.filter((r) => passesFilters(r, state));
}

function passesFilters(row: FilterableRow, state: FlightFilterState): boolean {
  if (state.outbound && row.effective_outbound_date != null) {
    if (row.effective_outbound_date < state.outbound[0] || row.effective_outbound_date > state.outbound[1]) {
      return false;
    }
  }
  if (state.inbound && row.effective_inbound_date != null) {
    if (row.effective_inbound_date < state.inbound[0] || row.effective_inbound_date > state.inbound[1]) {
      return false;
    }
  }
  const rowHasStopover = hasStopover(row);
  switch (state.stopover) {
    case 'direct':
      if (rowHasStopover) return false;
      break;
    case 'stopover':
      if (!rowHasStopover) return false;
      break;
  }
  if (rowHasStopover) {
    if (state.nights && row.stopover_nights != null) {
      if (row.stopover_nights < state.nights[0] || row.stopover_nights > state.nights[1]) {
        return false;
      }
    }
    if (state.leg && row.stopover_leg !== state.leg) {
      return false;
    }
  }
  if (state.carriers.length > 0 && !state.carriers.includes(row.carrier)) {
    return false;
  }
  return true;
}

/** Group already-filtered rows by composite direction group, preserving the server's price order. */
export function groupFilteredRows<T extends FilterableRow>(
  rows: T[],
  perShape: number,
): ShapeGroup<T>[] {
  const effectivePerShape = perShape < 1 ? 1 : perShape;
  const priced = rows.filter((r) => (r as unknown as { price: number | null }).price !== null);

  const byGroup = new Map<ItineraryGroupKey, T[]>();
  for (const r of priced) {
    const key = itineraryGroupKey(r);
    const group = byGroup.get(key);
    if (group) {
      group.push(r);
    } else {
      byGroup.set(key, [r]);
    }
  }

  const groups: ShapeGroup<T>[] = [];
  for (const [group_key, groupRows] of byGroup) {
    groups.push({
      group_key,
      itinerary_shape: groupRows[0].itinerary_shape,
      count: groupRows.length,
      rows: groupRows.slice(0, effectivePerShape),
    });
  }

  groups.sort((a, b) => {
    const aCheapest = (a.rows[0] as unknown as { price: number | null }).price ?? 0;
    const bCheapest = (b.rows[0] as unknown as { price: number | null }).price ?? 0;
    return aCheapest - bCheapest;
  });

  return groups;
}

/** Convert an ISO date to a zero-based day index relative to minIso. */
export function dayIndex(iso: string, minIso: string): number {
  return dateToDays(iso) - dateToDays(minIso);
}

/** Convert a day index back to an ISO date relative to minIso. */
export function isoFromDayIndex(index: number, minIso: string): string {
  return daysToDate(dateToDays(minIso) + index);
}

/**
 * Format an ISO date as "22/9" (no leading zeros).
 *
 * Defensive on input type. This is a *display* helper wired into a slider's
 * formatValue callback, and a slider hands back a number — passing this bare
 * threw `n.split is not a function` on first render, which unmounted the React
 * tree and left the flights page completely blank. A wrong label is a cosmetic
 * bug; a thrown exception in a render path takes down the whole route.
 */
export function formatDayMonth(iso: unknown): string {
  if (typeof iso !== 'string') return '—';
  const parts = iso.split('-');
  if (parts.length !== 3) return '—';
  const [, m, d] = parts;
  return `${Number(d)}/${Number(m)}`;
}

// Helpers below avoid Date objects; they rely on the fact that observations.db
// stores dates as YYYY-MM-DD in the Gregorian calendar.
function dateToDays(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  // Move March to month 0 so February is the last month of the previous year.
  const y1 = y - (m <= 2 ? 1 : 0);
  const m1 = (m + 9) % 12;
  const era = Math.floor(y1 / 400);
  const yoe = y1 - era * 400;
  const doy = Math.floor((153 * m1 + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe;
}

function daysToDate(days: number): string {
  const era = Math.floor(days / 146097);
  const doe = days - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  const year = y + (m <= 2 ? 1 : 0);
  return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
